import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, quotesApi, invoicesApi } from '../lib/api';
import { formatCurrency, formatDate, getStatusLabel } from '../lib/utils';
import { 
    FileText, 
    Receipt, 
    Package, 
    TrendingUp, 
    Euro,
    ArrowRight,
    Plus,
    Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function DashboardPage() {
    const [stats, setStats] = useState(null);
    const [recentQuotes, setRecentQuotes] = useState([]);
    const [recentInvoices, setRecentInvoices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [statsRes, quotesRes, invoicesRes] = await Promise.all([
                dashboardApi.getStats(),
                quotesApi.getAll(),
                invoicesApi.getAll(),
            ]);
            setStats(statsRes.data);
            setRecentQuotes(quotesRes.data.slice(0, 5));
            setRecentInvoices(invoicesRes.data.slice(0, 5));
        } catch (error) {
            console.error('Failed to load dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="stat-card h-24 sm:h-32 skeleton" />
                    ))}
                </div>
            </div>
        );
    }

    const statCards = [
        {
            title: 'Devis créés',
            value: stats?.quotes_count || 0,
            subValue: formatCurrency(stats?.total_quotes_value || 0),
            icon: FileText,
            color: 'text-blue-400',
            bgColor: 'bg-blue-400/10',
        },
        {
            title: 'Factures',
            value: stats?.invoices_count || 0,
            subValue: formatCurrency(stats?.total_revenue || 0),
            icon: Receipt,
            color: 'text-green-400',
            bgColor: 'bg-green-400/10',
        },
        {
            title: 'Produits',
            value: stats?.products_count || 0,
            subValue: 'Dans le catalogue',
            icon: Package,
            color: 'text-purple-400',
            bgColor: 'bg-purple-400/10',
        },
        {
            title: 'Marge totale',
            value: formatCurrency(stats?.total_margin || 0),
            subValue: 'Sur tous les devis',
            icon: TrendingUp,
            color: 'text-[#d4af37]',
            bgColor: 'bg-[#d4af37]/10',
        },
    ];

    return (
        <div className="space-y-4 sm:space-y-6 lg:space-y-8" data-testid="dashboard-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#f8fafc]">Tableau de bord</h1>
                    <p className="text-[#94a3b8] text-sm sm:text-base mt-1">Vue d'ensemble de votre activité</p>
                </div>
                <Link to="/quotes/new" className="w-full sm:w-auto">
                    <Button 
                        data-testid="new-quote-btn"
                        className="bg-[#d4af37] text-[#020617] hover:bg-[#fcd34d] font-semibold w-full sm:w-auto"
                    >
                        <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                        Nouveau devis
                    </Button>
                </Link>
            </div>

            {/* Stats Grid - 2 columns on mobile, 4 on desktop */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {statCards.map((stat, index) => (
                    <div 
                        key={stat.title}
                        className="stat-card p-3 sm:p-4 lg:p-5 animate-fadeInUp"
                        style={{ animationDelay: `${index * 0.1}s` }}
                        data-testid={`stat-card-${index}`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="min-w-0 flex-1">
                                <p className="text-[#94a3b8] text-xs sm:text-sm font-medium truncate">{stat.title}</p>
                                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-[#f8fafc] mt-1 mono truncate">
                                    {stat.value}
                                </p>
                                <p className="text-[#64748b] text-xs sm:text-sm mt-1 truncate hidden sm:block">{stat.subValue}</p>
                            </div>
                            <div className={`p-2 sm:p-3 rounded-lg ${stat.bgColor} flex-shrink-0 ml-2`}>
                                <stat.icon className={`w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 ${stat.color}`} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Revenue Overview + Recent Quotes */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                {/* Revenue - Full width on mobile, 1/3 on desktop */}
                <div className="stat-card p-4 sm:p-5">
                    <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                        <div className="p-2 rounded-lg bg-green-400/10">
                            <Euro className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                        </div>
                        <h3 className="text-base sm:text-lg font-semibold text-[#f8fafc]">Revenus</h3>
                    </div>
                    <div className="space-y-3 sm:space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-[#94a3b8] text-sm">Payé</span>
                            <span className="text-green-400 font-semibold mono text-sm sm:text-base">
                                {formatCurrency(stats?.paid_revenue || 0)}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-[#94a3b8] text-sm">En attente</span>
                            <span className="text-orange-400 font-semibold mono text-sm sm:text-base">
                                {formatCurrency(stats?.pending_revenue || 0)}
                            </span>
                        </div>
                        <div className="h-px bg-[#1e293b]" />
                        <div className="flex justify-between items-center">
                            <span className="text-[#f8fafc] font-medium text-sm sm:text-base">Total</span>
                            <span className="text-[#d4af37] font-bold mono text-sm sm:text-base">
                                {formatCurrency(stats?.total_revenue || 0)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Recent Quotes - Full width on mobile, 2/3 on desktop */}
                <div className="stat-card p-4 sm:p-5 lg:col-span-2">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="p-2 rounded-lg bg-blue-400/10">
                                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                            </div>
                            <h3 className="text-base sm:text-lg font-semibold text-[#f8fafc]">Derniers devis</h3>
                        </div>
                        <Link to="/documents" className="text-[#d4af37] hover:text-[#fcd34d] text-xs sm:text-sm flex items-center gap-1">
                            Voir tout <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Link>
                    </div>
                    {recentQuotes.length > 0 ? (
                        <div className="space-y-2 sm:space-y-3">
                            {recentQuotes.slice(0, 3).map((quote) => (
                                <div 
                                    key={quote.id}
                                    className="flex items-center justify-between p-2 sm:p-3 rounded-lg bg-[#020617]/50 hover:bg-[#1e293b]/50 transition-colors"
                                >
                                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[#1e293b] flex items-center justify-center flex-shrink-0">
                                            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-[#94a3b8]" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[#f8fafc] font-medium text-sm sm:text-base truncate">{quote.client_name}</p>
                                            <p className="text-[#64748b] text-xs sm:text-sm mono">{quote.quote_number}</p>
                                        </div>
                                    </div>
                                    <div className="text-right flex-shrink-0 ml-2">
                                        <p className="text-[#f8fafc] font-semibold mono text-sm sm:text-base">{formatCurrency(quote.total_sale)}</p>
                                        <span className={`status-badge status-${quote.status} text-xs`}>
                                            {getStatusLabel(quote.status)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-6 sm:py-8">
                            <FileText className="w-10 h-10 sm:w-12 sm:h-12 text-[#334155] mx-auto mb-2 sm:mb-3" />
                            <p className="text-[#64748b] text-sm">Aucun devis créé</p>
                            <Link to="/quotes/new">
                                <Button variant="link" className="text-[#d4af37] mt-2 text-sm">
                                    Créer votre premier devis
                                </Button>
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Recent Invoices */}
            {recentInvoices.length > 0 && (
                <div className="stat-card p-4 sm:p-5">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="p-2 rounded-lg bg-green-400/10">
                                <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                            </div>
                            <h3 className="text-base sm:text-lg font-semibold text-[#f8fafc]">Dernières factures</h3>
                        </div>
                        <Link to="/documents" className="text-[#d4af37] hover:text-[#fcd34d] text-xs sm:text-sm flex items-center gap-1">
                            Voir tout <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {recentInvoices.slice(0, 3).map((invoice) => (
                            <div 
                                key={invoice.id}
                                className="p-3 sm:p-4 rounded-lg bg-[#020617]/50 border border-[#1e293b] hover:border-[#d4af37]/30 transition-colors"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[#f8fafc] font-medium mono text-sm">{invoice.invoice_number}</span>
                                    <span className={`status-badge status-${invoice.status} text-xs`}>
                                        {getStatusLabel(invoice.status)}
                                    </span>
                                </div>
                                <p className="text-[#94a3b8] text-xs sm:text-sm truncate">{invoice.client_name}</p>
                                <p className="text-[#d4af37] font-semibold mt-2 mono text-sm sm:text-base">{formatCurrency(invoice.total_sale)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
